import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Play, Users, Archive, ArchiveRestore, Eye, EyeOff, X, ArrowDownUp } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const EMPTY = {
  tournament_id: "", team_a_id: "", team_b_id: "", venue_id: "",
  date: new Date().toISOString().slice(0, 10), time: "18:00",
  active_a: [], active_b: [],
};

export default function AdminGames() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [teams, setTeams] = useState([]);
  const [venues, setVenues] = useState([]);
  const [playersByTeam, setPlayersByTeam] = useState({});
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [rosterOpen, setRosterOpen] = useState(null); // game.id when picking active players
  const [showArchived, setShowArchived] = useState(false);
  const [sortBy, setSortBy] = useState("date_desc");
  const [teamFilter, setTeamFilter] = useState("all");
  const [selected, setSelected] = useState(new Set());

  const load = async () => {
    const [g, t, te, v, p] = await Promise.all([
      api.get(`/games?include_archived=${showArchived ? "true" : "false"}`),
      api.get("/tournaments"), api.get("/teams"), api.get("/venues"), api.get("/players"),
    ]);
    setItems(g.data); setTournaments(t.data); setTeams(te.data); setVenues(v.data);
    const byTeam = {};
    p.data.forEach((pl) => { byTeam[pl.team_id] = byTeam[pl.team_id] || []; byTeam[pl.team_id].push(pl); });
    setPlayersByTeam(byTeam);
  };
  useEffect(() => { load(); }, [showArchived]);

  const teamMap = useMemo(() => Object.fromEntries(teams.map((t) => [t.id, t])), [teams]);
  const teamsByTournament = useMemo(() => {
    const m = {};
    teams.forEach((t) => {
      const tids = (t.tournament_ids && t.tournament_ids.length ? t.tournament_ids : (t.tournament_id ? [t.tournament_id] : []));
      tids.forEach((tid) => { (m[tid] = m[tid] || []).push(t); });
    });
    return m;
  }, [teams]);

  const startCreate = () => { setEditing(null); setForm({ ...EMPTY, tournament_id: tournaments[0]?.id || "" }); setOpen(true); };
  const startEdit = (g) => { setEditing(g); setForm({ ...EMPTY, ...g }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.tournament_id || !form.team_a_id || !form.team_b_id) return toast.error("Tournament & both teams required");
    if (form.team_a_id === form.team_b_id) return toast.error("Teams must differ");
    try {
      if (editing) await api.put(`/games/${editing.id}`, form);
      else await api.post("/games", form);
      toast.success("Saved"); setOpen(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };

  const remove = async (g) => {
    if (!window.confirm("Delete this game and all actions?")) return;
    await api.delete(`/games/${g.id}`); toast.success("Deleted"); load();
  };

  const archive = async (g) => {
    await api.post(`/games/${g.id}/archive`);
    toast.success("Jogo arquivado");
    load();
  };

  const unarchive = async (g) => {
    await api.post(`/games/${g.id}/unarchive`);
    toast.success("Jogo restaurado");
    load();
  };

  // Reset selection when list changes (e.g. after archive/unarchive/toggle)
  useEffect(() => { setSelected(new Set()); }, [items]);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };

  const bulkArchive = async (archived) => {
    if (selected.size === 0) return;
    const r = await api.post("/games/bulk-archive", { ids: Array.from(selected), archived });
    toast.success(`${r.data.updated} jogo(s) ${archived ? "arquivados" : "restaurados"}`);
    setSelected(new Set());
    load();
  };

  // Sort + filter
  const displayItems = useMemo(() => {
    let arr = items;
    if (teamFilter !== "all") {
      arr = arr.filter((g) => g.team_a_id === teamFilter || g.team_b_id === teamFilter);
    }
    const cp = [...arr];
    const dateKey = (g) => `${g.date || "0000-00-00"} ${g.time || "00:00"}`;
    if (sortBy === "date_desc") cp.sort((a, b) => dateKey(b).localeCompare(dateKey(a)));
    else if (sortBy === "date_asc") cp.sort((a, b) => dateKey(a).localeCompare(dateKey(b)));
    else if (sortBy === "team_a") cp.sort((a, b) => (teamMap[a.team_a_id]?.name || "").localeCompare(teamMap[b.team_a_id]?.name || ""));
    else if (sortBy === "team_b") cp.sort((a, b) => (teamMap[a.team_b_id]?.name || "").localeCompare(teamMap[b.team_b_id]?.name || ""));
    else if (sortBy === "status") cp.sort((a, b) => (a.status || "").localeCompare(b.status || ""));
    return cp;
  }, [items, teamFilter, sortBy, teamMap]);

  const allSelected = displayItems.length > 0 && displayItems.every((g) => selected.has(g.id));
  const toggleSelectAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(displayItems.map((g) => g.id)));
  };

  return (
    <div className="p-6 md:p-10">
      <PageHeader
        title="Games"
        subtitle={isAdmin ? "Schedule fixtures and launch the live operator console" : "Pick a game to operate live"}
        testid="page-games-admin"
        action={isAdmin ? (
          <div className="flex flex-wrap gap-2">
            <Button
              data-testid="toggle-archived-btn"
              onClick={() => setShowArchived((v) => !v)}
              variant="outline"
              className="bg-transparent border-navy-border text-white hover:bg-navy-surface hover:text-orange font-heading uppercase tracking-widest rounded-sm"
            >
              {showArchived ? <><EyeOff size={16} className="mr-2" /> Ocultar arquivados</> : <><Eye size={16} className="mr-2" /> Mostrar arquivados</>}
            </Button>
            <Button data-testid="create-game-btn" onClick={startCreate} disabled={tournaments.length === 0 || teams.length < 2} className="bg-orange hover:bg-orange-hover text-white font-heading uppercase tracking-widest rounded-sm">
              <Plus size={16} className="mr-2" /> New game
            </Button>
          </div>
        ) : null}
      />

      {/* Filter & sort bar */}
      <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="games-toolbar">
        <div className="flex items-center gap-1 text-[10px] font-heading uppercase tracking-widest text-ink-muted">
          <ArrowDownUp size={12} /> Ordenar
        </div>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger data-testid="games-sort-select" className="bg-navy-deep border-navy-border text-white rounded-sm w-56 h-9">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-navy-deep border-navy-border text-white">
            <SelectItem value="date_desc">Mais recentes</SelectItem>
            <SelectItem value="date_asc">Mais antigos</SelectItem>
            <SelectItem value="team_a">Equipe A (A→Z)</SelectItem>
            <SelectItem value="team_b">Equipe B (A→Z)</SelectItem>
            <SelectItem value="status">Status</SelectItem>
          </SelectContent>
        </Select>
        <Select value={teamFilter} onValueChange={setTeamFilter}>
          <SelectTrigger data-testid="games-team-filter" className="bg-navy-deep border-navy-border text-white rounded-sm w-56 h-9">
            <SelectValue placeholder="Filtrar por equipe…" />
          </SelectTrigger>
          <SelectContent className="bg-navy-deep border-navy-border text-white max-h-80">
            <SelectItem value="all">Todas as equipes</SelectItem>
            {teams.map((tm) => <SelectItem key={tm.id} value={tm.id}>{tm.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted ml-auto" data-testid="games-count">
          {displayItems.length} jogo(s)
        </div>
      </div>

      {/* Bulk action bar */}
      {isAdmin && selected.size > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 bg-orange/10 border border-orange rounded-sm px-3 py-2" data-testid="bulk-action-bar">
          <span className="font-heading uppercase tracking-widest text-xs text-orange" data-testid="bulk-selected-count">
            {selected.size} selecionado(s)
          </span>
          <Button data-testid="bulk-archive-btn" onClick={() => bulkArchive(true)} className="bg-orange hover:bg-orange-hover text-white font-heading uppercase tracking-widest text-xs rounded-sm h-8 px-3 ml-2">
            <Archive size={14} className="mr-1" /> Arquivar
          </Button>
          <Button data-testid="bulk-unarchive-btn" onClick={() => bulkArchive(false)} variant="outline" className="bg-transparent border-orange text-orange hover:bg-orange hover:text-white font-heading uppercase tracking-widest text-xs rounded-sm h-8 px-3">
            <ArchiveRestore size={14} className="mr-1" /> Restaurar
          </Button>
          <Button data-testid="bulk-clear-btn" onClick={() => setSelected(new Set())} variant="ghost" className="text-ink-secondary hover:text-white font-heading uppercase tracking-widest text-xs rounded-sm h-8 px-2">
            <X size={14} className="mr-1" /> Limpar
          </Button>
        </div>
      )}

      <div className="border border-navy-border rounded-sm overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-2 bg-navy-surface text-[10px] font-heading uppercase tracking-widest text-ink-muted items-center">
          {isAdmin && (
            <div className="col-span-1">
              <input
                type="checkbox"
                data-testid="games-select-all"
                checked={allSelected}
                onChange={toggleSelectAll}
                className="cursor-pointer accent-orange w-4 h-4"
              />
            </div>
          )}
          <div className={isAdmin ? "col-span-2" : "col-span-2"}>Date</div>
          <div className={isAdmin ? "col-span-2" : "col-span-3"}>Team A</div>
          <div className={isAdmin ? "col-span-2" : "col-span-3"}>Team B</div>
          <div className="col-span-1">Score</div>
          <div className="col-span-1">Status</div>
          <div className={isAdmin ? "col-span-3 text-right" : "col-span-2 text-right"}>Actions</div>
        </div>
        {displayItems.length === 0 ? <div className="p-8 text-center text-ink-muted">No games yet</div> :
          displayItems.map((g) => (
            <div key={g.id} className={`grid grid-cols-12 items-center px-4 py-3 border-t border-navy-border ${g.admin_archived ? "opacity-60" : ""} ${selected.has(g.id) ? "bg-orange/5" : ""}`} data-testid={`game-row-${g.id}`}>
              {isAdmin && (
                <div className="col-span-1">
                  <input
                    type="checkbox"
                    data-testid={`game-select-${g.id}`}
                    checked={selected.has(g.id)}
                    onChange={() => toggleSelect(g.id)}
                    className="cursor-pointer accent-orange w-4 h-4"
                  />
                </div>
              )}
              <div className={`${isAdmin ? "col-span-2" : "col-span-2"} font-mono text-ink-secondary text-sm`}>
                {g.date}
                <div className="text-[10px] text-ink-muted">{g.time || ""}</div>
                {g.admin_archived && (
                  <span className="mt-1 inline-block px-1.5 py-0.5 text-[9px] font-heading uppercase tracking-widest rounded-sm bg-navy-surface text-ink-muted border border-navy-border" data-testid={`game-archived-badge-${g.id}`}>
                    Arquivado
                  </span>
                )}
              </div>
              <div className={`${isAdmin ? "col-span-2" : "col-span-3"} font-heading uppercase tracking-tight text-white`}>{teamMap[g.team_a_id]?.name || "?"}</div>
              <div className={`${isAdmin ? "col-span-2" : "col-span-3"} font-heading uppercase tracking-tight text-white`}>{teamMap[g.team_b_id]?.name || "?"}</div>
              <div className="col-span-1 font-mono tabular text-orange">{g.score_a}-{g.score_b}</div>
              <div className="col-span-1">
                <span className={`text-[10px] font-heading uppercase tracking-widest px-2 py-0.5 rounded-sm ${
                  g.status === "live" ? "bg-court-red text-white" :
                  g.status === "finished" ? "bg-navy-surface text-ink-secondary" :
                  g.status === "overtime" ? "bg-orange text-white" :
                  "bg-transparent border border-navy-border text-ink-muted"
                }`}>{g.status}</span>
              </div>
              <div className={`${isAdmin ? "col-span-3" : "col-span-2"} flex justify-end gap-1 flex-wrap`}>
                <Button data-testid={`roster-${g.id}`} size="sm" variant="ghost" onClick={() => setRosterOpen(g)} className="text-orange hover:bg-navy-surface text-xs">
                  <Users size={14} className="mr-1" /> {((g.active_a||[]).length + (g.active_b||[]).length)}/8
                </Button>
                <Link to={`/operator/${g.id}`} data-testid={`operate-${g.id}`}>
                  <Button size="sm" className="bg-orange hover:bg-orange-hover text-white font-heading uppercase text-xs tracking-widest rounded-sm">
                    <Play size={14} className="mr-1" /> Operate
                  </Button>
                </Link>
                {isAdmin && (
                  <>
                    {g.admin_archived ? (
                      <Button data-testid={`unarchive-game-${g.id}`} size="icon" variant="ghost" onClick={() => unarchive(g)} title="Restaurar" className="text-ink-secondary hover:text-court-green hover:bg-navy-surface"><ArchiveRestore size={14} /></Button>
                    ) : (
                      <Button data-testid={`archive-game-${g.id}`} size="icon" variant="ghost" onClick={() => archive(g)} title="Arquivar (interno)" className="text-ink-secondary hover:text-orange hover:bg-navy-surface"><Archive size={14} /></Button>
                    )}
                    <Button data-testid={`edit-game-${g.id}`} size="icon" variant="ghost" onClick={() => startEdit(g)} className="text-ink-secondary hover:text-orange hover:bg-navy-surface"><Pencil size={14} /></Button>
                    <Button data-testid={`delete-game-${g.id}`} size="icon" variant="ghost" onClick={() => remove(g)} className="text-ink-secondary hover:text-court-red hover:bg-navy-surface"><Trash2 size={14} /></Button>
                  </>
                )}
              </div>
            </div>
          ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-navy-deep border-navy-border text-white max-w-xl">
          <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight text-2xl">{editing ? "Edit game" : "New game"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="game-form">
            <div>
              <Label className="text-ink-secondary uppercase tracking-widest text-xs">Tournament</Label>
              <Select value={form.tournament_id} onValueChange={(v) => setForm({ ...form, tournament_id: v, team_a_id: "", team_b_id: "" })}>
                <SelectTrigger data-testid="game-tournament-select" className="bg-navy border-navy-border text-white mt-2 rounded-sm"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent className="bg-navy-deep border-navy-border text-white">
                  {tournaments.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-ink-secondary uppercase tracking-widest text-xs">Team A</Label>
                <Select value={form.team_a_id} onValueChange={(v) => setForm({ ...form, team_a_id: v })}>
                  <SelectTrigger data-testid="game-team-a-select" className="bg-navy border-navy-border text-white mt-2 rounded-sm"><SelectValue placeholder="Team A" /></SelectTrigger>
                  <SelectContent className="bg-navy-deep border-navy-border text-white">
                    {(teamsByTournament[form.tournament_id] || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-ink-secondary uppercase tracking-widest text-xs">Team B</Label>
                <Select value={form.team_b_id} onValueChange={(v) => setForm({ ...form, team_b_id: v })}>
                  <SelectTrigger data-testid="game-team-b-select" className="bg-navy border-navy-border text-white mt-2 rounded-sm"><SelectValue placeholder="Team B" /></SelectTrigger>
                  <SelectContent className="bg-navy-deep border-navy-border text-white">
                    {(teamsByTournament[form.tournament_id] || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-ink-secondary uppercase tracking-widest text-xs">Date</Label>
                <Input data-testid="game-date-input" type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="bg-navy border-navy-border mt-2 rounded-sm" />
              </div>
              <div>
                <Label className="text-ink-secondary uppercase tracking-widest text-xs">Time</Label>
                <Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="bg-navy border-navy-border mt-2 rounded-sm" />
              </div>
              <div>
                <Label className="text-ink-secondary uppercase tracking-widest text-xs">Venue</Label>
                <Select value={form.venue_id || ""} onValueChange={(v) => setForm({ ...form, venue_id: v })}>
                  <SelectTrigger className="bg-navy border-navy-border text-white mt-2 rounded-sm"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent className="bg-navy-deep border-navy-border text-white">
                    {venues.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="game-submit-btn" type="submit" className="bg-orange hover:bg-orange-hover text-white font-heading uppercase tracking-widest rounded-sm">{editing ? "Save" : "Create"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!rosterOpen} onOpenChange={(v) => !v && setRosterOpen(null)}>
        <DialogContent className="bg-navy-deep border-navy-border text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight text-2xl">Elenco ativo (4 por time · atletas fora do elenco não aparecerão no operador)</DialogTitle>
          </DialogHeader>
          {rosterOpen && (
            <RosterPicker
              game={rosterOpen}
              playersByTeam={playersByTeam}
              teamMap={teamMap}
              onSave={async (active_a, active_b) => {
                await api.post(`/games/${rosterOpen.id}/setup`, { active_a, active_b });
                toast.success("Roster saved");
                setRosterOpen(null);
                load();
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RosterPicker({ game, playersByTeam, teamMap, onSave }) {
  const [a, setA] = useState(game.active_a || []);
  const [b, setB] = useState(game.active_b || []);
  const toggle = (list, set, id, max = 4) => {
    if (list.includes(id)) set(list.filter((x) => x !== id));
    else if (list.length < max) set([...list, id]);
    else toast.error("Máximo 4 atletas ativos");
  };
  const gameTid = game.tournament_id;
  const eligibleForGame = (players) => (players || []).filter((p) => {
    const tids = (p.tournament_ids && p.tournament_ids.length ? p.tournament_ids : []);
    // If player has no tournament_ids at all, allow (legacy). Otherwise must include game's tournament.
    return tids.length === 0 || tids.includes(gameTid);
  });
  const playersA = eligibleForGame(playersByTeam[game.team_a_id]);
  const playersB = eligibleForGame(playersByTeam[game.team_b_id]);

  const Column = ({ players, list, set, label, testid }) => (
    <div>
      <h4 className="font-heading uppercase tracking-widest text-orange text-xs mb-2">{label} ({list.length}/4 · {players.length} elegíveis)</h4>
      <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
        {players.length === 0 ? <div className="text-ink-muted text-sm">Nenhum atleta elegível para este torneio</div> :
          players.map((p) => {
            const sel = list.includes(p.id);
            return (
              <button
                key={p.id}
                type="button"
                data-testid={`${testid}-${p.jersey_number}`}
                onClick={() => toggle(list, set, p.id)}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-sm border transition-all ${sel ? "border-orange bg-orange/10" : "border-navy-border bg-navy hover:border-white/40"}`}
              >
                <span className="font-mono tabular font-black text-orange text-xl w-8">{p.jersey_number}</span>
                <span className="font-heading uppercase tracking-tight text-white">{p.full_name}</span>
                {sel && <span className="ml-auto text-[10px] font-heading uppercase tracking-widest text-orange">Ativo</span>}
              </button>
            );
          })}
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-6">
        <Column players={playersA} list={a} set={setA} label={teamMap[game.team_a_id]?.name || "Team A"} testid="active-a" />
        <Column players={playersB} list={b} set={setB} label={teamMap[game.team_b_id]?.name || "Team B"} testid="active-b" />
      </div>
      <DialogFooter>
        <Button data-testid="save-roster-btn" onClick={() => onSave(a, b)} className="bg-orange hover:bg-orange-hover text-white font-heading uppercase tracking-widest rounded-sm">
          Salvar elenco
        </Button>
      </DialogFooter>
    </div>
  );
}
